// src/pages/campaigns/Campaigns.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  DateRange as DateRangeIcon,
  AssignmentTurnedIn as TasksIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import StatusDisplay from '../../components/ui/StatusDisplay';
import EntityStatusBadge from '../../components/core/EntityStatusBadge';
import ActionsMenu from '../../components/ui/ActionsMenu';
import { CampaignCard } from '../../components/core/cards';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useCampaignStore from '../../stores/campaignStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// Configuration for filter options
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'draft', label: 'Draft' },
      { value: 'planning', label: 'Planning' },
      { value: 'active', label: 'Active' },
      { value: 'paused', label: 'Paused' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ]
  },
  {
    name: 'type',
    label: 'Campaign Type',
    allLabel: 'All Types',
    options: [
      { value: 'awareness', label: 'Brand Awareness' },
      { value: 'engagement', label: 'Engagement' },
      { value: 'conversion', label: 'Conversion' },
      { value: 'product_launch', label: 'Product Launch' },
      { value: 'promotion', label: 'Promotion' },
      { value: 'event', label: 'Event' },
      { value: 'seasonal', label: 'Seasonal' },
      { value: 'brand_building', label: 'Brand Building' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    name: 'dateRange',
    label: 'Date Range',
    allLabel: 'All Time',
    options: [
      { value: 'current', label: 'Currently Running' },
      { value: 'upcoming', label: 'Upcoming' },
      { value: 'past', label: 'Past Campaigns' },
      { value: 'last30', label: 'Last 30 Days' },
      { value: 'next30', label: 'Next 30 Days' },
      { value: 'thisYear', label: 'This Year' }
    ]
  }
];

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'dates.plannedStartDate', label: 'Start Date' },
  { field: 'dates.plannedEndDate', label: 'End Date' },
  { field: 'budget.total', label: 'Budget' },
  { field: 'status', label: 'Status' },
  { field: 'createdAt', label: 'Date Created' }
];

/**
 * Campaigns listing page
 */
const Campaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query params if any
  const queryParams = new URLSearchParams(location.search);
  const clientId = queryParams.get('clientId');
  const brandId = queryParams.get('brandId');
  
  // Store hooks
  const { 
    campaigns, 
    meta, 
    isLoading, 
    error, 
    fetchCampaigns,
    fetchClientCampaigns,
    fetchBrandCampaigns,
    deleteCampaign 
  } = useCampaignStore();
  
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // State for action menus
  const [actionMenus, setActionMenus] = useState({});
  
  // Check permissions
  const canCreate = hasPermission('campaigns', 'create');
  const canUpdate = hasPermission('campaigns', 'update');
  const canDelete = hasPermission('campaigns', 'delete');
  
  // Handle campaign deletion with confirmation
  const handleDeleteCampaign = useCallback(async (campaign) => {
    const confirmed = await confirm({
      title: `Delete ${campaign.name}?`,
      content: `This will permanently delete the campaign and all associated data. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteCampaign(campaign._id);
        showSuccess(`Campaign "${campaign.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete campaign: ${error.message}`);
      }
    }
  }, [confirm, deleteCampaign, showSuccess, showError]);
  
  // Handle menu open/close
  const handleOpenMenu = useCallback((e, campaignId) => {
    e.stopPropagation();
    setActionMenus(prev => ({
      ...prev,
      [campaignId]: e.currentTarget
    }));
  }, []);
  
  const handleCloseMenu = useCallback((campaignId) => {
    setActionMenus(prev => ({
      ...prev,
      [campaignId]: null
    }));
  }, []);
  
  // Determine which fetch function to use based on URL params
  const fetchData = useCallback((params) => {
    if (brandId) {
      return fetchBrandCampaigns(brandId, params);
    } else if (clientId) {
      return fetchClientCampaigns(clientId, params);
    } else {
      return fetchCampaigns(params);
    }
  }, [brandId, clientId, fetchBrandCampaigns, fetchClientCampaigns, fetchCampaigns]);
  
  // Campaign renderer for grid view
  const renderCampaign = useCallback((campaign) => {
    return (
      <>
        <CampaignCard
          campaign={campaign}
          primaryAction={{
            label: 'View',
            icon: <ViewIcon />,
            onClick: () => navigate(`/campaigns/${campaign._id}`),
            variant: 'contained',
            color: 'primary'
          }}
          onEdit={canUpdate ? () => navigate(`/campaigns/${campaign._id}/edit`) : undefined}
          onDelete={canDelete ? () => handleDeleteCampaign(campaign) : undefined}
          onAnalytics={() => navigate(`/campaigns/${campaign._id}/analytics`)}
          onTasks={() => navigate(`/campaigns/${campaign._id}/tasks`)}
          linkTo={`/campaigns/${campaign._id}`}
        />
        
        {/* Actions menu with proper anchoring */}
        <ActionsMenu
          anchorEl={actionMenus[campaign._id]}
          open={Boolean(actionMenus[campaign._id])}
          onClose={() => handleCloseMenu(campaign._id)}
          actions={[
            {
              label: 'View Tasks',
              onClick: () => navigate(`/campaigns/${campaign._id}/tasks`),
            },
            {
              label: 'View Analytics',
              onClick: () => navigate(`/campaigns/${campaign._id}/analytics`),
            },
            ...(canUpdate ? [{
              label: 'Edit Campaign',
              onClick: () => navigate(`/campaigns/${campaign._id}/edit`),
            }] : [])
          ]}
          dangerActions={canDelete ? [
            {
              label: 'Delete Campaign', 
              onClick: () => handleDeleteCampaign(campaign)
            }
          ] : []}
        />
      </>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteCampaign, actionMenus, handleOpenMenu, handleCloseMenu]);
  
  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Campaign',
      renderCell: (campaign) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CampaignIcon sx={{ color: 'primary.main' }} />
          <Typography variant="body1" fontWeight={500}>
            {campaign.name}
          </Typography>
        </Box>
      )
    },
    { 
      id: 'type', 
      label: 'Type', 
      align: 'center',
      renderCell: (campaign) => {
        const formattedType = campaign.type?.charAt(0)?.toUpperCase() + campaign.type?.slice(1).replace('_', ' ') || 'Other';
        return (
          <StatusDisplay
            type="custom"
            customProps={{
              label: formattedType,
              color: "primary"
            }}
            size="small"
          />
        );
      }
    },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      renderCell: (campaign) => (
        <EntityStatusBadge
          status={campaign.status}
          entityType="campaign"
          size="small"
        />
      )
    },
    { 
      id: 'dates', 
      label: 'Timeline',
      align: 'center',
      renderCell: (campaign) => {
        const startDate = campaign.dates?.plannedStartDate 
          ? new Date(campaign.dates.plannedStartDate).toLocaleDateString() 
          : 'N/A';
        const endDate = campaign.dates?.plannedEndDate 
          ? new Date(campaign.dates.plannedEndDate).toLocaleDateString() 
          : 'N/A';
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <DateRangeIcon fontSize="small" />
            <Typography variant="body2">
              {startDate} - {endDate}
            </Typography>
          </Box>
        );
      }
    },
    { 
      id: 'budget', 
      label: 'Budget',
      align: 'right',
      renderCell: (campaign) => {
        const budget = campaign.budget?.total || 0;
        const currency = campaign.budget?.currency || 'USD';
        
        return (
          <Typography variant="body2" fontWeight={500}>
            {new Intl.NumberFormat('en-US', { 
              style: 'currency', 
              currency,
              maximumFractionDigits: 0
            }).format(budget)}
          </Typography>
        );
      }
    }
  ];
  
  // Configure table action buttons
  const getTableRowActions = useCallback((campaign) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="View Tasks">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/campaigns/${campaign._id}/tasks`);
            }}
          >
            <TasksIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Analytics">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/campaigns/${campaign._id}/analytics`);
            }}
          >
            <AnalyticsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/campaigns/${campaign._id}/edit`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCampaign(campaign);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteCampaign]);
  
  // Get appropriate page title and description
  const getTitle = () => {
    if (brandId) return 'Brand Campaigns';
    if (clientId) return 'Client Campaigns';
    return 'Campaigns';
  };
  
  const getDescription = () => {
    if (brandId) return 'Manage marketing campaigns for this brand';
    if (clientId) return 'Manage marketing campaigns for this client';
    return 'Manage all your marketing campaigns in one place';
  };
  
  return (
    <>
      <EntityListContainer
        title={getTitle()}
        description={getDescription()}
        fetchData={fetchData}
        renderEntity={renderCampaign}
        initialFilters={{
          status: 'all',
          type: 'all',
          dateRange: 'all',
        }}
        initialSort={{ field: 'dates.plannedStartDate', direction: 'desc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="grid"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (campaign) => navigate(`/campaigns/${campaign._id}`),
          renderRowActions: getTableRowActions
        }}
        
        // Use direct store values
        entities={campaigns}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: brandId 
            ? `/campaigns/create?brandId=${brandId}` 
            : clientId 
              ? `/campaigns/create?clientId=${clientId}` 
              : '/campaigns/create',
          createLabel: 'Create Campaign'
        }}
        emptyState={{
          title: brandId 
            ? 'No Campaigns For This Brand' 
            : clientId 
              ? 'No Campaigns For This Client' 
              : 'No Campaigns Yet',
          description: brandId 
            ? 'Get started by creating your first campaign for this brand.' 
            : clientId 
              ? 'Get started by creating your first campaign for this client.' 
              : 'Get started by creating your first marketing campaign.',
          icon: <CampaignIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Campaigns',
          getFilteredDescription: () => 'Try adjusting your search or filters to find what you\'re looking for.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default Campaigns;